import * as axios from "axios";

// Media Library v2 API — talks to the absinthe proxy (REACT_APP_API_SERVER),
// which forwards to video-api's /api/media/* with the server-side domain/brand
// headers. The browser never holds a video-api credential.
//
// Auth mirrors every other pubninja-ui call: the accessToken in the
// `authorization` header, and the session's active domain in the `domainId`
// query. absinthe verifies the user owns that domain before forwarding.

const BASE = process.env.REACT_APP_API_SERVER;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    accept: "application/json",
    authorization: localStorage.getItem("accessToken")
  };
}

// 1) Presign a staging upload. -> { stagingId, presignedUrl, r2Key }
export function requestUploadUrl(domainId, file) {
  return axios({
    method: "POST",
    url: `${BASE}/media/upload-url`,
    params: { domainId },
    headers: authHeaders(),
    data: {
      filename: file.name,
      contentType: file.type || "application/octet-stream",
      fileSize: file.size
    }
  }).then(function(res) {
    return res.data;
  });
}

// 2) PUT the raw File bytes straight to R2. NOT proxied through absinthe.
// Content-Type must match what upload-url was told, or the presign rejects it.
export function putFileToR2(presignedUrl, file, onProgress) {
  return axios({
    method: "PUT",
    url: presignedUrl,
    data: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
    // Do NOT send the app auth header to R2.
    transformRequest: [
      function(data) {
        return data;
      }
    ],
    onUploadProgress: function(evt) {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    }
  });
}

// 3) Kick off analysis (extract + detect + enrich + hash + dedup + preview).
export function startAnalyze(domainId, stagingId) {
  return axios({
    method: "POST",
    url: `${BASE}/media/analyze`,
    params: { domainId },
    headers: authHeaders(),
    data: { stagingId }
  }).then(function(res) {
    return res.data;
  });
}

// 4) Poll the analysis result.
export function getAnalysis(domainId, stagingId) {
  return axios({
    method: "GET",
    url: `${BASE}/media/analyze/${encodeURIComponent(stagingId)}`,
    params: { domainId },
    headers: authHeaders()
  }).then(function(res) {
    return res.data;
  });
}

// 5) Commit — the only write. `fields` is the edited flat map.
export function commit(domainId, payload) {
  return axios({
    method: "POST",
    url: `${BASE}/media/commit`,
    params: { domainId },
    headers: authHeaders(),
    // Relay a 422 { mandatoryMissing } to the caller instead of throwing away
    // the body — the dialog needs it to highlight the missing fields.
    validateStatus: function(s) {
      return (s >= 200 && s < 300) || s === 422;
    },
    data: payload
  }).then(function(res) {
    if (res.status === 422) {
      const data = res.data || {};
      // A blocked source URL returns { invalidSourceUrl, message }. Surface a GENERIC
      // message — never reveal the allowed-provider list to the user.
      if (data.invalidSourceUrl) {
        const urlErr = new Error("This URL can't be added to the media library.");
        urlErr.invalidSourceUrl = true;
        return Promise.reject(urlErr);
      }
      const err = new Error("mandatoryMissing");
      err.mandatoryMissing = (data.mandatoryMissing) || [];
      return Promise.reject(err);
    }
    return res.data;
  });
}

// 6) Cancel / abandon — discard staging (nothing was saved).
export function cancelStaging(domainId, stagingId) {
  return axios({
    method: "DELETE",
    url: `${BASE}/media/staging/${encodeURIComponent(stagingId)}`,
    params: { domainId },
    headers: authHeaders()
  }).then(function(res) {
    return res.data;
  });
}

// 7) Search saved assets. -> { items: [...], pagination, facets }
// keywords is sent comma-joined (the backend splits on comma) to avoid axios'
// bracketed array serialization, which the backend normalizer wouldn't parse.
export function searchMedia(domainId, opts) {
  const o = opts || {};
  return axios({
    method: "GET",
    url: `${BASE}/media/search`,
    params: {
      domainId: domainId,
      q: o.q || undefined,
      media_type: o.mediaType || undefined,
      source: o.source || undefined,
      editorial_only: o.editorial || undefined,
      created_from: o.from || undefined,
      created_to: o.to || undefined,
      keywords: o.keywords && o.keywords.length ? o.keywords.join(",") : undefined,
      // Comma-joined for the same reason as keywords: axios would serialize an
      // array as uploaded_by[]=… and the backend normalizer wouldn't parse it.
      uploaded_by: o.uploadedBy && o.uploadedBy.length ? o.uploadedBy.join(",") : undefined,
      sort: o.sort || undefined,
      page: o.page || 1,
      limit: o.limit || 30
    },
    headers: authHeaders()
  }).then(function(res) {
    return res.data;
  });
}

// 8) Autocomplete — titles + keyword tags for the search box. Returns empty
// lists for <2 chars (the backend guards it too).
export function suggestMedia(domainId, q) {
  var s = (q || "").trim();
  if (s.length < 2) return Promise.resolve({ titles: [], keywords: [] });
  return axios({
    method: "GET",
    url: `${BASE}/media/suggest`,
    params: { domainId: domainId, q: s },
    headers: authHeaders()
  }).then(function(res) {
    return res.data;
  });
}

// 9) Enrich from URL — scrape a source URL (Getty/AP article/generic) and return
// the details it yields, to fill blank review fields. -> { fields, resolvedUrl, found }
export function enrichMediaFromUrl(domainId, url, source) {
  return axios({
    method: "GET",
    url: `${BASE}/media/enrich-url`,
    params: { domainId: domainId, url: url, source: source || undefined },
    headers: authHeaders()
  }).then(function(res) {
    return res.data;
  });
}

// 10) Download — presigned R2 GET url that forces an attachment download of the
// original. -> { downloadUrl, filename, expiresInSeconds }
export function getMediaDownloadUrl(domainId, publicId) {
  return axios({
    method: "GET",
    url: `${BASE}/media/${encodeURIComponent(publicId)}/download`,
    params: { domainId: domainId },
    headers: authHeaders()
  }).then(function(res) {
    return res.data;
  });
}
